import React, {useState, useEffect, useRef} from 'react';
import {useParams} from 'react-router-dom';
import {v4 as uuidv4} from 'uuid';
import {orgId, defaultErrorHandler} from '../../context/context';
import customFormsV2Client from '../../infrastructure/acerill/custom-forms-v2-client';
import emailV1Client from '../../infrastructure/acerill/email-v1-client';
import notificationV1Client from '../../infrastructure/acerill/notification-v1-client';
import shopifyV1Client from '../../infrastructure/acerill/shopify-v1-client';
import fieldService from '../../services/field-service';
import Section from '../../libs/react-components-admin/section/section';
import CenteredSpinner from '../../libs/react-components-admin/spinners/centered-spinner';
import {TextField} from '../../libs/react-components-admin/fields/text-like-field';
import EntitySelector from '../../libs/react-components-admin/fields/entity-selector';
import SaveCancelBar from '../../libs/react-components-admin/buttons/save-cancel-bar';
import SubSection from '../../libs/react-components-admin/section/sub-section';
import NotificationType from './notification-type';
import Template from './template';
import EmailFieldIds from './email-field-ids';
import EmailAddresses from '../../libs/react-components-admin/email/email-addresses';

const subjectSampleTemplate = 'We have received your submission';

const bodySampleTemplate = 'Thank you for your submission.<br/>\n' +
  'We will be in touch shortly.<br/><br/>\n' +
  'The Team';

const FormNotification = () => {

  const {formId, notificationId} = useParams();

  const defaultEmailPromiseRef = useRef();

  const [notification, setNotification] = useState();
  const [subjectTemplate, setSubjectTemplate] = useState();
  const [bodyTemplate, setBodyTemplate] = useState();
  const [form, setForm] = useState();

  useEffect(() => {
    if (!notification) {
      customFormsV2Client.findFormNotificationById(formId, notificationId)
        .then(n => setNotification(n || {
          id: notificationId, type: 'email-to-org',
          templates: {subject: {templateId: uuidv4()}, body: {templateId: uuidv4()}},
        }))
        .catch(err => defaultErrorHandler(err));
    } else if (notification.type === 'email-to-org') {
      if (notification.emailFieldIds) {
        setNotification({...notification, emailFieldIds: null});
      } else if (!notification.emailAddresses) {
        getDefaultEmailPromise()
          .then(email => setNotification({...notification, emailAddresses: email ? [email] : []}))
          .catch(err => defaultErrorHandler(err));
      }
    } else if (notification.type === 'email-to-customer' && notification.emailAddresses) {
      setNotification({...notification, emailAddresses: null});
    }
  }, [notification]);

  useEffect(() => {
    customFormsV2Client.findFormById(formId)
      .then(f => setForm(f))
      .catch(err => defaultErrorHandler(err));
  }, []);

  const getDefaultEmailPromise = () => {
    if (!defaultEmailPromiseRef.current) {
      defaultEmailPromiseRef.current = orgId.endsWith('.myshopify.com') ?
        shopifyV1Client.findShopById(orgId)
          .then(({email}) => email)
          .catch(err => {
            defaultErrorHandler(err);
            return null;
          }) :
        Promise.resolve(null);
    }
    return defaultEmailPromiseRef.current;
  };

  const isNotificationValid = () => {
    if (!notification) {
      return false;
    }
    if (notification.type === 'email-to-org' && (notification.emailAddresses || []).length === 0) {
      return false;
    }
    if (notification.type === 'email-to-customer' && (notification.emailFieldIds || []).length === 0) {
      return false;
    }
    if (!subjectTemplate || !bodyTemplate) {
      return false;
    }
    return true;
  };

  const save = () => {
    const templates = notification.templates;
    return Promise
      .all([
        notificationV1Client.saveTemplate({id: templates.subject.templateId, content: subjectTemplate}),
        notificationV1Client.saveTemplate({id: templates.body.templateId, content: bodyTemplate}),
      ])
      .then(() => customFormsV2Client.saveFormNotification(formId, notification));
  };

  const nonStaticFields = form => (form.fields || [])
    .filter(f => !fieldService.isStaticFieldType(f.fieldType));

  return <Section title="Form Notification" noMargin={true}>
    <div className="row justify-content-center mb-5">
      <div className="col-12 col-md-8">
        {!notification || !form ?
          <CenteredSpinner/> :
          <>
            <NotificationType value={notification.type}
                              onChange={v => setNotification({...notification, type: v})}/>
            {notification.type === 'email-to-customer' &&
              <EmailFieldIds form={form}
                             value={notification.emailFieldIds}
                             onChange={v => setNotification({...notification, emailFieldIds: v})}/>
            }
            {notification.type === 'email-to-org' &&
              (notification.emailAddresses == null ?
              <CenteredSpinner/> :
              <div className="mb-3">
                <label className="form-label">
                  Email addresses
                  <div className="form-text" style={{marginTop: 0}}>List of recipients who will receive the notification</div>
                </label>
                <div className="ms-md-5">
                  <EmailAddresses minEmailAddresses={1}
                                  initialModel={notification.emailAddresses}
                                  onChange={v => setNotification({...notification, emailAddresses: v})}/>
                </div>
              </div>
              )
            }
            <TextField id="name"
                       label="Name"
                       placeholder={notification.type === 'email-to-customer' ?
                         'e.g. Email to customer' :
                         'e.g. Email to sales department'
                       }
                       helpBlock={<>Helps you identify this notification</>}
                       value={notification.name}
                       onChange={value => setNotification({...notification, name: value})}/>
            <EntitySelector id="smtpConfigId"
                            label="SMTP config"
                            fetchEntities={() => emailV1Client.findSmtpConfigSummaries()}
                            errorHandler={defaultErrorHandler}
                            allowNull={true}
                            hideWhenNoChoice={true}
                            initialValue={notification.smtpConfigId}
                            onChange={v => setNotification({...notification, smtpConfigId: v})}/>
            <SubSection title="Email subject" className="mb-3">
              <Template rowCount={1}
                        id={notification.templates.subject.templateId}
                        sampleTemplate={notification.type === 'email-to-customer' ? subjectSampleTemplate : null}
                        fields={nonStaticFields(form)}
                        onChange={v => setSubjectTemplate(v)}/>
            </SubSection>
            <SubSection title="Email body" className="mb-3">
              <Template rowCount={8}
                        id={notification.templates.body.templateId}
                        sampleTemplate={notification.type === 'email-to-customer' ? bodySampleTemplate : null}
                        fields={nonStaticFields(form)}
                        onChange={v => setBodyTemplate(v)}/>
            </SubSection>
            <SaveCancelBar saveAction={() => save()}
                           saveDisabled={!isNotificationValid()}
                           cancelAction={() => new Promise(resolve => {
                             setNotification(null);
                             resolve();
                           })}
                           errorHandler={defaultErrorHandler}/>
          </>
        }
      </div>
    </div>
  </Section>;
};

export default FormNotification;